import moment from "moment";
import { get } from "lodash";
import { CHART_CUSTOM_RANGE_OPTION } from "constant";
import { convertSecondsToPrometheusTime } from "utils/functions";
import { TimeRangeSelection } from "components/common/TimeRangeDropdown";

type SensorBusReading = {
    UID: string;
    value: any;
    max: any;
    min: any;
    ts: any;
};

export const createDefaultLast24HoursTimeRange = (): TimeRangeSelection => {
    const now = moment();
    return {
        range: "Last 24 Hours",
        startDate: now.clone().subtract(1, "day"),
        endDate: now,
    };
};

export const getPrometheusTimeRangeForSensorReadings = (
    timeRange: TimeRangeSelection
): { lastTime: string; offsetTime?: string } => {
    // Default: use selected window duration
    let lastTimeValue = Math.round(
        moment.duration(timeRange.endDate.diff(timeRange.startDate)).asSeconds()
    );

    let offsetTime: string | undefined;

    // Custom Range: offset is relative to "now" so the backend query aligns properly.
    if (timeRange.range === CHART_CUSTOM_RANGE_OPTION) {
        lastTimeValue = Math.round(
            moment.duration(moment().diff(timeRange.endDate)).asSeconds()
        );

        offsetTime = convertSecondsToPrometheusTime(lastTimeValue);

        // Keep lastTime (range length) based on the actual start/end selection.
        lastTimeValue = moment
            .duration(timeRange.endDate.diff(timeRange.startDate))
            .asSeconds();
    }

    const lastTime = convertSecondsToPrometheusTime(lastTimeValue);
    return { lastTime, offsetTime };
};

const normalizeCandidate = (candidate: any): any => {
    // Keep backend "missing" values consistent with UI ("- / -")
    return candidate ?? "-";
};

const isValidNumericCandidate = (candidate: any): boolean => {
    if (candidate == null) return false;
    if (candidate === "-") return false;
    return !Number.isNaN(Number(candidate));
};

export const pickBestSensorBusReadingForSAID = (
    sensor: any,
    allReadings: SensorBusReading[]
): { highest: any; lowest: any; value: any; timestamp: any } | null => {
    if (!sensor) return null;

    let bestReading:
        | { highest: any; lowest: any; value: any; timestamp: any }
        | null = null;
    let bestScore = -1;

    for (const reading of allReadings) {
        if (reading?.UID !== sensor.ldsu_uuid) continue;

        const highestCandidate = normalizeCandidate(get(reading.max, `[${sensor.SAID}]`, "-"));
        const lowestCandidate = normalizeCandidate(get(reading.min, `[${sensor.SAID}]`, "-"));
        const valueCandidate = normalizeCandidate(get(reading.value, `[${sensor.SAID}]`, "-"));

        const score =
            (isValidNumericCandidate(highestCandidate) ? 1 : 0) +
            (isValidNumericCandidate(lowestCandidate) ? 1 : 0);

        if (score > bestScore) {
            bestScore = score;
            bestReading = {
                highest: highestCandidate,
                lowest: lowestCandidate,
                value: valueCandidate,
                timestamp: reading.ts,
            };
        }
    }

    return bestReading;
};

export const mergeSensorLastReadingWithMinMaxFallback = (
    prevReadings: any,
    candidates: { highest: any; lowest: any; value: any; timestamp: any }
): {
    highest: any;
    lowest: any;
    timestamp: any;
    value: any;
    minmax: [any, any];
} => {
    const prev = prevReadings ?? {};

    const highestFinal =
        candidates.highest === "-" ? (prev.highest ?? "-") : candidates.highest;
    const lowestFinal =
        candidates.lowest === "-" ? (prev.lowest ?? "-") : candidates.lowest;
    const valueFinal =
        candidates.value === "-" ? (prev.value ?? "-") : candidates.value;
    const timestampFinal = candidates.timestamp ?? prev.timestamp;

    return {
        highest: highestFinal,
        lowest: lowestFinal,
        timestamp: timestampFinal,
        value: valueFinal,
        minmax: [highestFinal, lowestFinal],
    };
};

